import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../api/client'
import type { StoreDetails, StoreDetailsResponse } from '../../api/types'
import { formatCount, formatDate, formatRelative } from '../../lib/format'
import { Segmented } from '../Segmented'

const languageNames = new Intl.DisplayNames(undefined, { type: 'language' })

function formatBytes(bytes: number | null): string | null {
	if (!bytes) return null
	const mb = bytes / 1_000_000
	return mb >= 1000 ? `${(mb / 1000).toFixed(1)} GB` : `${Math.round(mb)} MB`
}

function languageList(codes: string[]): string {
	const names = codes.map((c) => {
		try {
			return languageNames.of(c.toLowerCase()) ?? c
		} catch {
			return c
		}
	})
	return names.length > 6 ? `${names.slice(0, 5).join(', ')} and ${names.length - 5} more` : names.join(', ')
}

function Facts({ d }: { d: StoreDetails }) {
	const price =
		d.formattedPrice ?? (d.price === 0 ? 'Free' : d.price !== null ? `${d.price} ${d.currency ?? ''}` : null)
	const facts: [string, string | null][] = [
		['Price', price],
		['Category', d.genres.join(', ') || null],
		['Age rating', d.contentRating],
		['Size', formatBytes(d.fileSizeBytes)],
		['Requires', d.minimumOsVersion && `OS ${d.minimumOsVersion} or later`],
		['Released', d.releaseDate && formatDate(d.releaseDate)],
		['Updated', d.currentVersionReleaseDate && formatDate(d.currentVersionReleaseDate)],
		['Languages', d.languages.length ? languageList(d.languages) : null],
		['Seller', d.sellerName],
		['Game Center', d.gameCenter ? 'Yes' : null],
		[
			'Store rating',
			d.averageUserRating !== null
				? `${d.averageUserRating.toFixed(2)} ★ from ${formatCount(d.userRatingCount)} ratings`
				: null,
		],
	]
	return (
		<dl className="facts">
			{facts
				.filter(([, value]) => value)
				.map(([label, value]) => (
					<div key={label}>
						<dt>{label}</dt>
						<dd>{value}</dd>
					</div>
				))}
		</dl>
	)
}

type Device = keyof StoreDetails['screenshots']
const DEVICE_LABELS: Record<Device, string> = { iphone: 'iPhone', ipad: 'iPad', appletv: 'Apple TV' }

function Screenshots({ shots }: { shots: StoreDetails['screenshots'] }) {
	const kinds = (['iphone', 'ipad', 'appletv'] as const).filter((k) => shots[k].length)
	const [kind, setKind] = useState<Device>()
	const selected = kind ?? kinds[0]
	if (!selected) return null
	return (
		<div className="stack" style={{ gap: '0.5rem' }}>
			{kinds.length > 1 && (
				<div style={{ alignSelf: 'flex-start' }}>
					<Segmented<Device>
						label="Screenshot device"
						value={selected}
						options={kinds.map((k) => ({ value: k, label: DEVICE_LABELS[k] }))}
						onChange={setKind}
					/>
				</div>
			)}
			<div className="screenshots">
				{shots[selected].map((url, i) => (
					<a key={url} href={url} target="_blank" rel="noreferrer">
						<img src={url} alt={`${DEVICE_LABELS[selected]} screenshot ${i + 1}`} loading="lazy" />
					</a>
				))}
			</div>
		</div>
	)
}

/** The app's App Store listing, refreshed from Apple by the server when it's a day old. */
export function AboutPanel({ appId }: { appId: number }) {
	const [expanded, setExpanded] = useState(false)
	const store = useQuery({
		queryKey: ['app-store', appId],
		queryFn: () => api.get<StoreDetailsResponse>(`/api/v1/apps/${appId}/store`),
		// The server answers before a slow lookup finishes; check back for the fresh listing.
		refetchInterval: (q) => (q.state.data?.refreshing ? 5000 : false),
	})
	const d = store.data?.details
	return (
		<section className="card stack">
			<div className="row" style={{ justifyContent: 'space-between' }}>
				<h2 style={{ margin: 0 }}>About</h2>
				<span className="muted small">
					{store.data?.refreshing
						? 'Updating from the App Store…'
						: store.data?.fetchedAt
							? `${store.data.storefront?.toUpperCase()} App Store · checked ${formatRelative(store.data.fetchedAt)}`
							: null}
				</span>
			</div>
			{store.isPending ? (
				<div className="empty">Loading…</div>
			) : store.isError ? (
				<div className="empty">Couldn’t load the App Store listing.</div>
			) : !d ? (
				<div className="empty">
					{store.data.refreshing ? 'Fetching the App Store listing…' : 'No App Store listing found for this app.'}
				</div>
			) : (
				<>
					<Facts d={d} />
					{(d.developerUrl || d.sellerUrl) && (
						<div className="row small">
							{d.developerUrl && (
								<a href={d.developerUrl} target="_blank" rel="noreferrer">
									More from this developer ↗
								</a>
							)}
							{d.sellerUrl && (
								<a href={d.sellerUrl} target="_blank" rel="noreferrer">
									Developer website ↗
								</a>
							)}
						</div>
					)}
					{d.advisories.length > 0 && (
						<p className="muted small" style={{ margin: 0 }}>
							{d.advisories.join(' · ')}
						</p>
					)}
					{d.description && (
						<div>
							<p className={`description ${expanded ? '' : 'clamped'}`}>{d.description}</p>
							<button type="button" className="link-button" onClick={() => setExpanded(!expanded)}>
								{expanded ? 'Show less' : 'Show full description'}
							</button>
						</div>
					)}
					<Screenshots shots={d.screenshots} />
				</>
			)}
		</section>
	)
}
